import {
  AddRounded,
  DeleteOutlineRounded,
  EditRounded,
  NotificationsNoneRounded,
  SearchRounded,
  UploadFileRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scheme } from "../../models/schemes";
import Loading from "../../../../components/loading/loading";
import useUser from "../../../../providers/user/user_provider";
import { black, blue, blueLight, greenLight, greyEnabled, purple, purpleLight, red, redLight } from "../../../../theme/colors";
import FilterIcon from "../../../../assets/filter_icon.svg";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";
import { ToolbarButton } from "../../../../components/toolbar_button/toolbar_button";
import OrgSelector from "../../../../components/org_selector/org_selector";
import SortIcon from "../../../../assets/sort.svg";
import {
  SchemesSortOption,
  schemesSortOptionLabel,
} from "../sort_schemes_popup/sort_schemes_popup";
import BulkUploadSchemesDialog from "../bulk_upload_schemes/bulk_upload_schemes_dialog";

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

interface SchemeListing {
  searchList: Scheme[];
  deleteScheme: boolean;
  viewSchemeDetails: boolean;
  selectedSchemeDetails: {};
  searchValue: string;
  setSelectedSchemeDetails: (val: Scheme) => void;
  setDeleteScheme: (val: boolean) => void;
  setViewSchemeDetails: (val: boolean) => void;
  setSearchValue: (val: string) => void;
  count: number;
  page: number;
  setPage: (val: number) => void;
  pageSize: number;
  setPageSize: (val: number) => void;
  isLoading: boolean;
  isError?: boolean;
  setNotificationPopUp: (val: boolean) => void;
  sortOption: SchemesSortOption;
  onOpenSort: () => void;
  activeFilterCount: number;
  onOpenFilters: () => void;
}

export default function SchemesListing({
  searchList,
  setSelectedSchemeDetails,
  setDeleteScheme,
  setSearchValue,
  searchValue,
  count,
  page,
  setPage,
  pageSize,
  setPageSize,
  isLoading,
  isError = false,
  setNotificationPopUp,
  sortOption,
  onOpenSort,
  activeFilterCount,
  onOpenFilters,
}: SchemeListing) {
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const startRange = (page - 1) * pageSize + 1;
  const endRange = Math.min(page * pageSize, count);
  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;
  const showList = !isLoading && !isError && searchList && searchList.length > 0;
  const showEmpty = !isLoading && !isError && (!searchList || searchList.length === 0);

  return (
    <Box display={"flex"} flexDirection={"column"} sx={{ height: "100%" }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          mb={2}
        >
          <Box maxWidth={{ xs: "100%", sm: 360 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
            <TextField
              value={searchValue}
              onChange={handleSearchChange}
              size="small"
              fullWidth
              placeholder="Search by scheme name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded sx={{ color: greyEnabled, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <OrgSelector lockToOwnOrg />
            <ToolbarButton
              icon={SortIcon}
              label={schemesSortOptionLabel(sortOption)}
              onClick={onOpenSort}
            />
            <ToolbarButton
              icon={FilterIcon}
              label="Filters"
              badgeCount={activeFilterCount}
              onClick={onOpenFilters}
            />
            {hasPermission("schemes", "create") && (
              <Button
                onClick={() => setBulkUploadDialogOpen(true)}
                variant="outlined"
                disableElevation
                startIcon={<UploadFileRounded sx={{ height: 18, width: 18 }} />}
                sx={{
                  borderColor: purple,
                  color: purple,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#7A2074", bgcolor: purpleLight },
                }}
              >
                Bulk Upload
              </Button>
            )}
            {hasPermission("schemes", "create") && (
              <Button
                onClick={() => navigate("/schemes/add")}
                variant="contained"
                disableElevation
                startIcon={<AddRounded sx={{ height: 18, width: 18 }} />}
                sx={{
                  bgcolor: purple,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#7A2074" },
                }}
              >
                Add Scheme
              </Button>
            )}
          </Box>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Loading />
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            py={8}
            gap={0.5}
          >
            <Typography fontWeight={600} color={black}>
              Something went wrong
            </Typography>
            <Typography variant="body2" color={greyEnabled}>
              We couldn't load the schemes list. Please try again.
            </Typography>
          </Box>
        ) : showEmpty ? (
          <TableEmptyState
            message="No schemes found"
            hint="Try adjusting your search, or add a new scheme."
          />
        ) : (
          <Box mt={1}>
            <TableHeaderRow
              labels={[
                { label: "Sl. No.", flex: 0.2 },
                { label: "Scheme Id", flex: 0.35 },
                { label: "Scheme", flex: 1.95 },
                { label: "Actions" },
              ]}
              lastColumnWidth={190}
              align="left"
              bordered
            />
            {searchList?.map((item, index) => (
              <TableRow key={item.schemeId} bordered>
                <TableCell label="Sl. No." flex={0.2} bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {(page - 1) * pageSize + index + 1}
                  </Typography>
                </TableCell>
                <TableCell label="Scheme Id" flex={0.35} bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {item?.schemeId}
                  </Typography>
                </TableCell>
                <TableCell label="Scheme" flex={1.95} bordered>
                  <Typography fontWeight={600} color={black} noWrap>
                    {item?.name}
                  </Typography>
                  <Box display="flex" flexDirection="row" flexWrap="wrap" mt={0.25}>
                    <Typography variant="body2" color={purple} fontWeight={600} mr={1}>
                      {item?.governmentType}
                    </Typography>
                    {item.categories?.map((categoryItem) => (
                      <Typography key={categoryItem} variant="body2" color={greyEnabled} mr={1}>
                        {"| " + categoryItem}
                      </Typography>
                    ))}
                  </Box>
                </TableCell>
                <TableCell isLast lastColumnWidth={190} bordered>
                <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }} gap={0.5}>
                  <Tooltip title="View details">
                    <IconButton
                      onClick={() => navigate(`/schemes/${item.schemeId}`)}
                      sx={{ bgcolor: greenLight, "&:hover": { bgcolor: "#D6EEE0" } }}
                    >
                      <VisibilityRounded sx={{ color: "#1E6446", fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  {hasPermission("schemes", "update") && (
                    <Tooltip title="Edit scheme">
                      <IconButton
                        onClick={() => navigate(`/schemes/edit/${item.schemeId}`)}
                        sx={{ bgcolor: purpleLight, "&:hover": { bgcolor: "#F0D9F0" } }}
                      >
                        <EditRounded sx={{ color: purple, fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasPermission("schemes", "update") && (
                    <Tooltip title="Notify users">
                      <IconButton
                        onClick={() => {
                          setSelectedSchemeDetails(item);
                          setNotificationPopUp(true);
                        }}
                        sx={{ bgcolor: blueLight, "&:hover": { bgcolor: "#D7E3F4" } }}
                      >
                        <NotificationsNoneRounded sx={{ color: blue, fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasPermission("schemes", "delete") && (
                    <Tooltip title="Delete scheme">
                      <IconButton
                        onClick={() => {
                          setDeleteScheme(true);
                          setSelectedSchemeDetails?.(item);
                        }}
                        sx={{ bgcolor: redLight, "&:hover": { bgcolor: "#FBD9D6" } }}
                      >
                        <DeleteOutlineRounded sx={{ color: red, fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                </TableCell>
              </TableRow>
            ))}
          </Box>
        )}
      </CardContent>

      {showList && (
        <CardActions sx={{ px: 4, pb: 3, mt: "auto" }}>
          <Box width="100%">
            <TableFooterPagination
              count={count}
              page={page - 1}
              onPageChange={handleChangePage}
              summaryText={customText}
              rowsPerPage={pageSize}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={setPageSize}
            />
          </Box>
        </CardActions>
      )}

      <BulkUploadSchemesDialog
        open={bulkUploadDialogOpen}
        onClose={() => setBulkUploadDialogOpen(false)}
      />
    </Box>
  );
}
